import { db } from "@/api/db/db.index";
import { sharedContentTopics } from "@/api/db/schema/content";
import { messages } from "@/api/db/schema/message";
import {
  insertTagschema,
  moderateForumQuestionSchema,
  patchTagschema,
  tags,
} from "@/api/db/schema/tag";
import { communityService } from "@/api/services/community.service";
import { slugify } from "@/utils/slug";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import z from "zod";
import { deleteTag } from "../../api/services/tags/delete";
import { getAllTags, getSelectedTag } from "../../api/services/tags/get";
import { updateTag } from "../../api/services/tags/patch";
import { createTag } from "../../api/services/tags/post";
import {
  authMiddlewareServer,
  optionalAuthMiddlewareServer,
} from "../middleware/auth";

export const getAllTagsFn = createServerFn({ method: "GET" })
  .middleware([])
  .handler(async () => {
    const tags = await getAllTags();
    return {
      status: 200,
      body: { tags },
    };
  });

export const getSelectTagFn = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      tagId: z.string().min(1, "Tag ID is required"),
    }),
  )
  .middleware([optionalAuthMiddlewareServer])
  .handler(async ({ context, data }) => {
    const tag = await getSelectedTag(data.tagId);
    if (
      tag &&
      tag.moderationStatus !== "published" &&
      tag.moderationStatus !== "merged" &&
      context.auth?.userId !== tag.createdId &&
      context.auth?.role !== "admin" &&
      context.auth?.role !== "author"
    ) {
      return { status: 404, body: { tag: null } };
    }
    return {
      status: 200,
      body: { tag },
    };
  });

export const getSelectedTagOptions = ({ tagId }: { tagId: string }) =>
  queryOptions({
    queryKey: ["getSelectedTag", tagId],
    queryFn: () => getSelectTagFn({ data: { tagId } }),
  });

export const createTagFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertTagschema)
  .handler(async ({ context, data }) => {
    const user = context.auth;
    if (user.role !== "admin" && user.role !== "author") {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const recent = await db.query.tags.findMany({
        where: (table, { and, eq, gte }) =>
          and(eq(table.createdId, user.userId), gte(table.createdAt, since)),
        columns: { tagId: true },
      });
      if (recent.length >= 5) {
        throw new Error("24 saat içinde en fazla 5 soru oluşturabilirsiniz");
      }
    }
    const tag = await createTag(data, user.userId);
    await communityService.setFollowing(
      user.userId,
      "forum_question",
      tag.tagId,
      true,
    );
    return {
      status: 201,
      body: { tag },
    };
  });

export const moderateForumQuestionFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(moderateForumQuestionSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin" && context.auth.role !== "author") {
      throw new Error("Unauthorized");
    }
    if (data.status === "merged" && !data.mergedIntoId) {
      throw new Error("Birleştirilen soru için hedef soru gereklidir");
    }
    const existing = await db.query.tags.findFirst({
      where: eq(tags.tagId, data.tagId),
    });
    if (!existing) throw new Error("Soru bulunamadı");
    let slug = existing.slug;
    if (data.status === "published" && !slug) {
      const base = slugify(existing.tagTitle) || `soru-${existing.tagId}`;
      slug = base;
      let suffix = 2;
      while (await db.query.tags.findFirst({ where: eq(tags.slug, slug) })) {
        slug = `${base}-${suffix++}`;
      }
    }
    const [question] = await db
      .update(tags)
      .set({
        slug,
        moderationStatus: data.status,
        mergedIntoId: data.status === "merged" ? data.mergedIntoId : null,
        approvedAt: data.status === "published" ? new Date() : null,
        approvedBy: data.status === "published" ? context.auth.userId : null,
      })
      .where(eq(tags.tagId, data.tagId))
      .returning();
    if (!question) throw new Error("Soru bulunamadı");
    return { question };
  });

export const listForumModerationFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin" && context.auth.role !== "author") {
      throw new Error("Unauthorized");
    }
    const questions = await db.query.tags.findMany({
      with: {
        creater: { columns: { userName: true } },
        messages: { columns: { id: true, isExpertAnswer: true } },
      },
      orderBy: (table, { desc }) => [
        desc(table.lastActivityAt),
        desc(table.createdAt),
      ],
    });
    return {
      questions: questions.map((question) => ({
        ...question,
        messageCount: question.messages.length,
        hasExpertAnswer: question.messages.some(
          (message) => message.isExpertAnswer,
        ),
        messages: undefined,
      })),
    };
  });

export const updateForumEditorialFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(
    z.object({
      tagId: z.string().min(1),
      topics: z.array(z.enum(sharedContentTopics)),
      ctaHref: z
        .string()
        .trim()
        .regex(/^\/(?!\/)/)
        .nullable(),
      ctaLabel: z.string().trim().max(80).nullable(),
    }),
  )
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin" && context.auth.role !== "author") {
      throw new Error("Unauthorized");
    }
    const [question] = await db
      .update(tags)
      .set({
        topics: data.topics,
        ctaHref: data.ctaHref,
        ctaLabel: data.ctaLabel,
      })
      .where(eq(tags.tagId, data.tagId))
      .returning();
    if (!question) throw new Error("Soru bulunamadı");
    return { question };
  });

export const acceptForumAnswerFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(
    z.object({ tagId: z.string(), messageId: z.string().nullable() }),
  )
  .handler(async ({ context, data }) => {
    const question = await db.query.tags.findFirst({
      where: eq(tags.tagId, data.tagId),
    });
    if (!question) throw new Error("Soru bulunamadı");
    if (
      question.createdId !== context.auth.userId &&
      context.auth.role !== "admin"
    ) {
      throw new Error("Unauthorized");
    }
    if (data.messageId) {
      const answer = await db.query.messages.findFirst({
        where: and(
          eq(messages.id, data.messageId),
          eq(messages.tagId, data.tagId),
        ),
      });
      if (!answer) throw new Error("Yanıt bu soruya ait değil");
    }
    await db
      .update(tags)
      .set({ acceptedMessageId: data.messageId })
      .where(eq(tags.tagId, data.tagId));
    return { acceptedMessageId: data.messageId };
  });

export const patchTagFn = createServerFn({ method: "POST" })
  .inputValidator(patchTagschema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    const user = context.auth;

    const tag = await updateTag(data, user.userId);
    return {
      status: 200,
      body: { tag },
    };
  });

export const deleteTagn = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      tagId: z.string().min(1, "Tag ID is required"),
    }),
  )
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    const user = context.auth;

    const tag = await deleteTag(data.tagId, user.userId);
    return {
      status: 200,
      body: { tag },
    };
  });
